'use client';

import React from 'react';
import { cn } from '@workspace/lib';
import { INVISIBLES } from '@workspace/constants';
import { HeroBannerBackground } from './HeroBannerBackground';
import { HeroBannerContent } from './HeroBannerContent';
import { HeroBannerSlide as HeroBannerSlideType, SanityImage } from '@workspace/interfaces';
import { HeroBannerLayout } from '../hero-banner.types';
import { heroBannerVariants } from '@workspace/blocks/HeroBannerCarousel/heroBanner.variants';

type NormalizedHeroBannerSlide = Omit<HeroBannerSlideType, 'heading'> & {
  heading: {
    _type: string;
    fontSize?: 'h1' | 'h2' | 'h3';
    headingText: string;
  };
  videoFile?: SanityImage | null;
  bynderVideo?: any;
  poster?: SanityImage | null;
};

interface HeroBannerSlideProps {
  block: NormalizedHeroBannerSlide;
  isActive: boolean; // kept for a11y, but not used for animations
  size: 'slim' | 'medium' | 'full';
  className?: string;
  shouldPreloadImage?: boolean;
  layout?: HeroBannerLayout;
}

export function HeroBannerSlide({
  block,
  isActive,
  size,
  className,
  shouldPreloadImage = false,
  layout = 'overlay',
}: HeroBannerSlideProps) {
  // Helpers
  const cleanContentPosition = (pos?: string): 'left' | 'center' | 'right' => {
    if (!pos || typeof pos !== 'string') return 'left';
    const cleaned = pos.replace(INVISIBLES, '').trim().toLowerCase();
    return ['left', 'center', 'right'].includes(cleaned) ? (cleaned as 'left' | 'center' | 'right') : 'left';
  };

  const positionClassMap: Record<'left' | 'center' | 'right', string> = {
    left: '',
    center: 'flex-col items-center text-center',
    right: 'flex-col items-end text-right',
  };

  if (!block) {
    return (
      <div className="embla__slide shrink-0 grow-0 basis-full relative w-full h-full overflow-hidden bg-gray-200 flex items-center justify-center">
        <div className="text-gray-500 text-center">
          <h2 className="text-2xl font-bold mb-2">Content Loading...</h2>
          <p className="text-sm">Please wait while we load the content.</p>
        </div>
      </div>
    );
  }

  const contentPosition = cleanContentPosition(block?.contentPosition);
  const blockImage = block?.image;
  const { slide, backgroundWrapper, foreground } = heroBannerVariants({
    layout,
    size,
    contentPosition,
  });

  return (
    <div
      className={cn(
        'embla__slide relative w-full shrink-0 grow-0 basis-full',
        layout === 'article'
          ? 'porta-container flex h-auto min-h-0 flex-col items-start overflow-visible md:flex-row md:items-center md:justify-between'
          : 'h-full overflow-hidden',
        slide(),
      )}
      role="group"
      aria-roledescription="slide"
      aria-hidden={!isActive}
      tabIndex={isActive ? 0 : -1}
    >
      {/* Static background (no parallax/transition) */}
      <div
        className={cn(
          layout === 'article'
            ? 'relative inset-auto order-1 w-full shrink-0 self-center overflow-hidden md:order-2 md:w-auto'
            : 'absolute inset-0',
          backgroundWrapper(),
        )}
      >
        <HeroBannerBackground
          blockImage={blockImage}
          contentPosition={contentPosition}
          isVideo={block.isVideo}
          videoUrl={block.videoUrl}
          videoFile={block.videoFile}
          bynderVideo={block.bynderVideo}
          poster={block.poster}
          isActive={isActive}
          shouldPreload={shouldPreloadImage}
          size={size}
          showGradient={block.showGradient}
          focalPoint={block.focalPoint}
          layout={layout}
        />
      </div>

      {/* Static foreground content */}
      <div
        className={cn(
          'relative z-1 h-max items-center',
          layout === 'overlay' && 'porta-container',
          layout === 'article' &&
            'order-2 h-auto min-h-0 w-full shrink-0 self-stretch md:order-1 md:w-auto md:self-auto',
          positionClassMap[contentPosition],
          foreground(),
          className,
        )}
      >
        <HeroBannerContent
          tag={block.tag}
          heading={block.heading}
          description={block.description}
          richDescription={block.content}
          contentPosition={contentPosition}
          textColor={block.textColor}
          bynderTopImage={block.bynderTopImage}
          buttons={block.buttons}
          socialLinks={block.socialLinks}
          shareIcons={block.shareIcons}
          authorBlog={block.authorBlog}
          publicDataBlog={block.publicDataBlog}
          size={size}
          layout={layout}
        />
      </div>
    </div>
  );
}
